import React from "react";
import { motion } from "framer-motion";
import { Send, ShieldCheck, Sparkles, CalendarDays, Rocket } from "lucide-react";

const ICONS = {
  send: Send,
  "shield-check": ShieldCheck,
  sparkles: Sparkles,
  calendar: CalendarDays,
  rocket: Rocket,
};

const COLOR_CLASS = {
  success: "bg-[color:rgba(47,231,161,0.12)] text-[color:var(--success)] ring-[color:rgba(47,231,161,0.35)]",
  info: "bg-[color:rgba(77,183,255,0.12)] text-[color:var(--info)] ring-[color:rgba(77,183,255,0.35)]",
  primary: "bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] ring-[color:rgba(27,168,159,0.35)]",
  warn: "bg-[color:rgba(255,138,61,0.12)] text-[color:var(--accent-warm)] ring-[color:rgba(255,138,61,0.35)]",
  secondary: "bg-[color:rgba(154,230,110,0.10)] text-[color:var(--brand-secondary)] ring-[color:rgba(154,230,110,0.35)]",
};

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.round(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch (_e) {
    return "";
  }
}

export default function ActivityFeed({ events, emptyText = "No recent activity." }) {
  if (!events || events.length === 0) {
    return <div className="text-sm text-[color:var(--text-3)]" data-testid="activity-empty">{emptyText}</div>;
  }
  return (
    <ol className="relative pl-5" data-testid="activity-feed">
      <span className="absolute left-2 top-1 bottom-1 w-px bg-[color:var(--border-subtle)]" aria-hidden="true" />
      {events.map((e, i) => {
        const Icon = ICONS[e.icon] || Sparkles;
        const cls = COLOR_CLASS[e.color] || COLOR_CLASS.primary;
        return (
          <motion.li
            key={e.id || i}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.03, duration: 0.25 }}
            className="relative pl-4 pb-4 last:pb-0"
            data-testid="activity-row"
          >
            <span className={"absolute -left-[3px] top-1 h-2.5 w-2.5 rounded-full ring-2 " + cls} />
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm text-[color:var(--text-1)] font-medium">
                  <Icon className="h-3.5 w-3.5 opacity-80" />
                  {e.title}
                  {e.source === "autopilot" ? (
                    <span className="text-[10px] uppercase tracking-wider text-[color:var(--brand-secondary)]">auto</span>
                  ) : null}
                </div>
                {e.subtitle ? (
                  <div className="text-xs text-[color:var(--text-3)] mt-0.5 truncate">{e.subtitle}</div>
                ) : null}
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)] shrink-0">{timeAgo(e.at)}</span>
            </div>
          </motion.li>
        );
      })}
    </ol>
  );
}
